import { useCallback, useMemo, useState } from "react";
import { Send } from "lucide-react";
import { StyleSheet, View, Text, ScrollView, Pressable } from "react-native";

import { AddToMessage } from "../../interfaceComponents/addToMessage/AddToMessage";
import { AttachmentChip } from "../../interfaceComponents/attachmentChip/AttachmentChip";
import CustomButton from "../../../common/general/CustomButton/CustomButton";
import GrowingTextInput from "./GrowingTextInput";
import CustomText from "../../../common/general/CustomText/CustomText";

interface SuggestedPrompt {
  text: string;
  color: string;
  textColor?: string;
}

interface AttachedItem {
  id: string;
  name: string;
  duedate?: string | number | Date;
}

interface AttachedFile {
  id: string;
  fileName: string;
  fileURL?: string;
  isLoading?: boolean;
}

interface MessageComposerProps {
  onSendMessage: () => void;
  message: string;
  setMessage: (msg: string) => void;
  addTask?: () => void;
  removeTask?: (taskId: string) => void;
  addAssignment?: () => void;
  removeAssignment?: (assignmentId: string) => void;
  addFile?: () => void;
  removeFile?: (fileId: string) => void;
  attachedTasks?: AttachedItem[];
  attachedAssignments?: AttachedItem[];
  attachedFiles?: AttachedFile[];
  suggestedPrompts?: SuggestedPrompt[];
  placeholder?: string;
  disabled?: boolean;
}

export const MessageComposer: React.FC<MessageComposerProps> = ({
  onSendMessage,
  suggestedPrompts = [],
  placeholder = "Type your message...",
  message,
  setMessage,
  addAssignment,
  removeAssignment,
  addFile,
  removeFile,
  addTask,
  removeTask,
  attachedAssignments = [],
  attachedFiles = [],
  attachedTasks = [],
  disabled = false,
}: MessageComposerProps) => {
  const [isActive, setIsActive] = useState(false);

  const attachedItems = useMemo(
    () => [
      ...attachedAssignments.map((assignment) => ({
        ...assignment,
        type: "assignment" as const,
        isLoading: false,
      })),
      ...attachedTasks.map((task) => ({
        ...task,
        type: "task" as const,
        isLoading: false,
      })),
      ...attachedFiles.map((file) => ({
        id: file.id,
        name: file.fileName,
        type: "file" as const,
        isLoading: file.isLoading ?? false,
        duedate: undefined,
      })),
    ],
    [attachedAssignments, attachedTasks, attachedFiles]
  );

  const removeAttachment = useCallback(
    (id: string, type: "task" | "assignment" | "file") => {
      if (type === "task" && removeTask) {
        removeTask(id);
      } else if (type === "assignment" && removeAssignment) {
        removeAssignment(id);
      } else if (type === "file" && removeFile) {
        removeFile(id);
      }
    },
    [removeTask, removeAssignment, removeFile]
  );

  return (
    <View style={styles.container}>
      {suggestedPrompts.length > 0 && (
        <View style={styles.overflowHidden}>
          <ScrollView horizontal>
            <View style={styles.suggestedPromptsWrapper}>
              {suggestedPrompts.map((prompt, index) => (
                <Pressable
                  key={index}
                  style={[styles.promptItem, { backgroundColor: prompt.color }]}
                  onPress={() => {
                    setMessage(prompt.text);
                  }}>
                  <CustomText
                    size="s"
                    useTranslationText={false}
                    text={prompt.text}
                    style={{ color: prompt.textColor ?? "#000" }}
                    numberOfLines={1}
                  />
                </Pressable>
              ))}
            </View>
          </ScrollView>
        </View>
      )}
      <View style={[styles.messageContainer, isActive && styles.activeMessageContainer]}>
        {attachedItems.length > 0 && (
          <View style={styles.overflowHidden}>
            <ScrollView horizontal style={styles.attachedItemsScrollViewWrapper}>
              <View style={styles.attachedItemsWrapper}>
                {attachedItems.map((item) => (
                  <AttachmentChip
                    key={item.id}
                    name={item.name}
                    type={item.type}
                    duedate={item.duedate}
                    isLoading={item.isLoading}
                    removeChip={() => {
                      removeAttachment(item.id, item.type);
                    }}
                  />
                ))}
              </View>
            </ScrollView>
          </View>
        )}
        <View style={styles.messageWrapper}>
          <AddToMessage onAddAssignment={addAssignment} onAddTask={addTask} onAddAttachment={addFile} />
          <GrowingTextInput
            value={message}
            setValue={setMessage}
            placeholder={placeholder}
            onKeySubmit={onSendMessage}
            onFocus={() => setIsActive(true)}
            onBlur={() => setIsActive(false)}
          />
          <CustomButton
            leftIcon={<Send size={20} color="#fff" />}
            disabled={message === "" || disabled}
            style={[styles.sendButton, message === "" && styles.disabledSendButton]}
            onPress={onSendMessage}
          />
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: "column",
    alignItems: "flex-end",
  },
  suggestedPromptsWrapper: {
    display: "flex",
    flexDirection: "row",
    gap: 8,
    paddingBottom: 12,
  },
  promptItem: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 999,
  },
  messageContainer: {
    display: "flex",
    alignSelf: "stretch",
    flexDirection: "column",
    borderRadius: 32,
    borderWidth: 1,
    borderColor: "#e5e7eb",
    backgroundColor: "#ffffff",
    marginTop: 16,
  },
  activeMessageContainer: {
    borderColor: "#5177fe",
    shadowColor: "#ccd7ff",
    shadowOffset: {
      width: 0,
      height: 1,
    },
    shadowOpacity: 1,
    shadowRadius: 8,
    elevation: 3,
  },
  messageWrapper: {
    alignSelf: "stretch",
    flexDirection: "row",
    alignItems: "flex-end",
    padding: 8,
    gap: 8,
  },
  attachedItemsScrollViewWrapper: {
    padding: 12,
    paddingBottom: 8,
    borderBottomWidth: 1,
    borderBottomColor: "#e0e0e0",
  },
  attachedItemsWrapper: {
    display: "flex",
    flexDirection: "row",
    gap: 8,
    marginBottom: 8,
  },
  sendButton: {
    borderRadius: 99,
    padding: 8,
    aspectRatio: 1,
    backgroundColor: "#3e68fe",
  },
  disabledSendButton: {
    opacity: 0.5,
  },
  overflowHidden: {
    alignSelf: "stretch",
    overflow: "hidden",
  },
});
